import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom, forkJoin } from 'rxjs';
import { HD_GATE_INFO_EAGER_SCOPES } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HD_PLANET_INFO_SCOPES } from '@/app/human-design/chart/bodygraph/hd-planet-info.service';
import { HD_PHS_INFO_SCOPES } from '@/app/human-design/chart/bodygraph/hd-phs-info.service';
import { HdBodygraphSvgService } from '@/app/human-design/chart/bodygraph/hd-bodygraph-chart/hd-bodygraph-svg.service';

/**
 * Hoe lang het opstartscherm hoogstens op de warm-up wacht, gerekend vanaf de
 * start ervan (die loopt parallel met de platform-init). Ruim onder de 5 s
 * waarna het opstartscherm zijn herlaad-knop toont.
 */
export const STARTUP_WARMUP_MAX_WAIT_MS = 3000;

/**
 * Lettertypes van het eerste scherm (CSS-font-shorthand voor
 * `document.fonts.load`). Browsers downloaden een webfont pas wanneer tekst hem
 * nodig heeft; zonder warm-up verscheen de chart eerst met fallback-letters en
 * zonder planeet-glyphs (Astronomicon). Gemeten bij de eerste chart-opening
 * (2026-10-13): Modelica 400/700/800/900, Astronomicon en Font Awesome Duotone;
 * Roboto en Font Awesome Solid gebruikt elk scherm.
 */
export const STARTUP_FONTS: readonly string[] = [
  '400 1em Roboto',
  '500 1em Roboto',
  '700 1em Roboto',
  '400 1em "Bw Modelica"',
  '700 1em "Bw Modelica"',
  '800 1em "Bw Modelica"',
  '900 1em "Bw Modelica"',
  '1em Astronomicon',
  '900 1em "Font Awesome 7 Pro"',
  '900 1em "Font Awesome 7 Duotone"',
];

/** De HD-vertalingen die de chart-pagina bij het openen laadt (zelfde lijsten als de services). */
export const STARTUP_HD_SCOPES: readonly string[] = [
  ...HD_GATE_INFO_EAGER_SCOPES,
  ...HD_PLANET_INFO_SCOPES,
  ...HD_PHS_INFO_SCOPES,
];

/**
 * Laadt tijdens het opstartscherm wat het eerste scherm meteen nodig heeft:
 * lettertypes, de vertalingen in de actieve taal (app + Human Design) en de
 * bodygraph-svg. Zonder dit begon dat pas bij de eerste chart-opening, zichtbaar
 * als verspringende letters, lege info-blokken en ontbrekende glyphs.
 *
 * Wat geladen is, blijft in het geheugen (Transloco, HdBodygraphSvgService,
 * `document.fonts`) en in de browsercache: componenten die het later vragen,
 * krijgen het meteen.
 */
@Injectable({ providedIn: 'root' })
export class StartupWarmupService {
  private readonly transloco = inject(TranslocoService);
  private readonly bodygraphSvg = inject(HdBodygraphSvgService);

  /**
   * Start alle downloads. Resolvet zodra alles binnen is of uiterlijk na
   * `maxWaitMs`; trage downloads lopen daarna gewoon door. Faalt nooit: wie een
   * mislukt asset later nodig heeft, vraagt het zelf opnieuw op.
   */
  start(maxWaitMs = STARTUP_WARMUP_MAX_WAIT_MS): Promise<void> {
    const lang = this.transloco.getActiveLang();
    const everything = Promise.allSettled([
      this.loadFonts(),
      this.loadTranslations(lang),
      this.bodygraphSvg.load(),
    ]).then(() => undefined);
    const deadline = new Promise<void>(resolve => setTimeout(resolve, maxWaitMs));
    return Promise.race([everything, deadline]);
  }

  private loadFonts(): Promise<unknown> {
    const fonts = document.fonts;
    if (!fonts) {
      return Promise.resolve();
    }

    return Promise.allSettled(STARTUP_FONTS.map(font => fonts.load(font)));
  }

  private loadTranslations(lang: string): Promise<unknown> {
    const paths = [lang, ...STARTUP_HD_SCOPES.map(scope => `${scope}/${lang}`)];
    return firstValueFrom(forkJoin(paths.map(path => this.transloco.load(path))));
  }
}
